import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { X, Camera, Loader2, User } from 'lucide-react';
import { useUpdateProfile } from '@/hooks/useUser';
import { useAuth } from '@/providers/AuthProvider';
import { getSdk } from '@/lib/sdk';
import { processAvatarUrl } from '@/lib/mediaUtils';

interface EditProfileProps {
  onClose: () => void;
}

export default function EditProfile({ onClose }: EditProfileProps) {
  const { user } = useAuth();
  const userRecord = user as Record<string, unknown> | null;
  const userUlid = userRecord?.ulid as string | undefined;

  const [displayName, setDisplayName] = useState((userRecord?.displayName as string) || (userRecord?.name as string) || '');
  const [bio, setBio] = useState((userRecord?.bio as string) || '');
  const [website, setWebsite] = useState((userRecord?.website as string) || '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const updateMutation = useUpdateProfile();

  const rawAvatar = (userRecord?.avatarUrl as string) || (userRecord?.avatar as string) || '';
  const currentAvatar = processAvatarUrl(rawAvatar) || '';
  const currentCover = processAvatarUrl((userRecord?.background as string) || '') || '';

  useEffect(() => {
    gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power2.out' });
    gsap.fromTo(sheetRef.current, { y: '100%' }, { y: '0%', duration: 0.35, ease: 'power3.out' });
  }, []);

  const animateClose = useCallback((cb: () => void) => {
    gsap.to(sheetRef.current, { y: '100%', duration: 0.25, ease: 'power3.in' });
    gsap.to(overlayRef.current, { opacity: 0, duration: 0.25, ease: 'power2.in', onComplete: cb });
  }, []);

  const handleClose = useCallback(() => animateClose(onClose), [animateClose, onClose]);

  const handleImageSelect = (type: 'avatar' | 'cover') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (type === 'avatar') { setAvatarPreview(ev.target?.result as string); setAvatarFile(file); }
      else { setCoverPreview(ev.target?.result as string); setCoverFile(file); }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!userUlid) return;
    setIsUploading(true);
    try {
      const sdk = getSdk();
      const payload: Parameters<typeof updateMutation.mutateAsync>[0] = {};

      if (displayName.trim()) payload.displayName = displayName.trim();
      if (bio !== undefined) payload.bio = bio;
      if (website !== undefined) payload.website = website;

      if (avatarFile) {
        const result = await sdk.uploadMediaFile(avatarFile, { userUlid, mediaType: 'image' });
        payload.avatarUrl = result.url;
      }
      if (coverFile) {
        const result = await sdk.uploadMediaFile(coverFile, { userUlid, mediaType: 'image' });
        payload.background = result.url;
      }

      await updateMutation.mutateAsync(payload);
      animateClose(onClose);
    } catch {
      /* error handled by mutation state */
    } finally {
      setIsUploading(false);
    }
  };

  const isSaving = isUploading || updateMutation.isPending;

  return createPortal(
    <div ref={overlayRef} className="fixed inset-0 z-[95] bg-black/70" onClick={handleClose}>
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 bg-[#0F1117] rounded-t-[24px] flex flex-col"
        style={{ maxHeight: '92vh', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-[#2A2F3A] rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] flex-shrink-0">
          <button onClick={handleClose} aria-label="Cancel" className="w-9 h-9 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]">
            <X size={18} />
          </button>
          <h2 className="text-base font-semibold text-[#F5F7FF]" style={{ fontFamily: 'Space Grotesk' }}>Edit Profile</h2>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${isSaving ? 'bg-[#A38BFF]/30 text-[#A38BFF]/50 cursor-not-allowed' : 'bg-[#A38BFF] text-[#0B0D10] active:scale-95'}`}
          >
            {isSaving ? <Loader2 size={16} className="animate-spin" /> : 'Save'}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Cover image */}
          <div className="relative h-32 bg-[#141821] group cursor-pointer" onClick={() => coverInputRef.current?.click()}>
            {(coverPreview || currentCover) ? (
              <img src={coverPreview || currentCover} alt="Cover" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#A38BFF]/20 to-[#141821]" />
            )}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera size={24} className="text-white" />
            </div>
            <div className="absolute bottom-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center">
              <Camera size={16} className="text-white" />
            </div>
          </div>

          {/* Avatar */}
          <div className="px-4 pb-4">
            <div className="relative -mt-10 mb-4 w-20 h-20 group cursor-pointer" onClick={() => avatarInputRef.current?.click()}>
              {(avatarPreview || currentAvatar) ? (
                <img src={avatarPreview || currentAvatar} alt="Avatar" className="w-20 h-20 rounded-full object-cover border-4 border-[#0F1117]" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-[#141821] border-4 border-[#0F1117] flex items-center justify-center">
                  <User size={28} className="text-[#A7B0C8]" />
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera size={20} className="text-white" />
              </div>
              <div className="absolute bottom-0 right-0 w-7 h-7 bg-[#A38BFF] rounded-full flex items-center justify-center border-2 border-[#0F1117]">
                <Camera size={13} className="text-[#0B0D10]" />
              </div>
            </div>

            {/* Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#A7B0C8] mb-1.5">Display name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={50}
                  placeholder="Your name"
                  className="w-full h-11 bg-[#141821] rounded-xl px-4 text-sm text-[#F5F7FF] placeholder-[#A7B0C8]/40 outline-none focus:ring-1 focus:ring-[#A38BFF]/50 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#A7B0C8] mb-1.5">Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  maxLength={160}
                  rows={3}
                  placeholder="Tell people about yourself"
                  className="w-full bg-[#141821] rounded-xl px-4 py-3 text-sm text-[#F5F7FF] placeholder-[#A7B0C8]/40 outline-none focus:ring-1 focus:ring-[#A38BFF]/50 transition-all resize-none"
                />
                <p className="text-xs text-[#A7B0C8] text-right mt-1">{bio.length}/160</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#A7B0C8] mb-1.5">Website</label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://yoursite.com"
                  className="w-full h-11 bg-[#141821] rounded-xl px-4 text-sm text-[#F5F7FF] placeholder-[#A7B0C8]/40 outline-none focus:ring-1 focus:ring-[#A38BFF]/50 transition-all"
                />
              </div>

              {updateMutation.isError && (
                <p className="text-xs text-[#FF4D4D]">Failed to save. Please try again.</p>
              )}
            </div>
          </div>
        </div>

        {/* Hidden file inputs */}
        <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect('avatar')} />
        <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect('cover')} />
      </div>
    </div>,
    document.body
  );
}
